<template>
  <v-layout align-start>
    <v-flex>
      <!-- LISTA DE ACTIVIDADES -->
      <template v-if="!editing">
        <div class="activities-page">
          <div class="page-header">
            <span class="header-badge">Agenda</span>
            <h1 class="page-title">Gestión de Actividades</h1>
            <p class="page-subtitle">Crear, editar y eliminar actividades del partido.</p>
          </div>

          <div class="controls-bar">
            <div class="tabs-wrapper">
              <button
                class="tab-btn"
                :class="{ active: tab === 'todas' }"
                @click="tab = 'todas'"
              >Todas</button>
              <button
                class="tab-btn"
                :class="{ active: tab === 'puntuales' }"
                @click="tab = 'puntuales'"
              >Puntuales</button>
              <button
                class="tab-btn"
                :class="{ active: tab === 'recurrentes' }"
                @click="tab = 'recurrentes'"
              >Recurrentes</button>
              <button
                class="tab-btn tab-btn--pending"
                :class="{ active: tab === 'pendientes' }"
                @click="tab = 'pendientes'; getPending()"
              >
                Pendientes
                <span v-if="pendingActivities.length > 0" class="pending-badge">{{ pendingActivities.length }}</span>
              </button>
            </div>
            <div class="controls-right">
              <v-text-field
                v-model="search"
                prepend-inner-icon="search"
                placeholder="Buscar actividad..."
                single-line
                hide-details
                dense
                outlined
                class="search-field"
              ></v-text-field>
              <button class="create-btn" @click="create()">
                + Nueva Actividad
              </button>
            </div>
          </div>

          <!-- Modal activar / desactivar / eliminar -->
          <v-dialog v-model="adModal" max-width="400">
            <v-card>
              <v-card-title v-if="adAccion == 1">Activar Actividad</v-card-title>
              <v-card-title v-if="adAccion == 2">Desactivar Actividad</v-card-title>
              <v-card-title v-if="adAccion == 3">Eliminar Actividad</v-card-title>
              <v-card-text>
                Estas a punto de
                <span v-if="adAccion == 1"><strong>activar</strong></span>
                <span v-if="adAccion == 2"><strong>desactivar</strong></span>
                <span v-if="adAccion == 3"><strong>eliminar</strong></span>
                la actividad <strong>{{ adNombre }}</strong>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="adModal = false" text>Cancelar</v-btn>
                <v-btn v-if="adAccion == 1" @click="activar()" color="success" text>Activar</v-btn>
                <v-btn v-if="adAccion == 2" @click="desactivar()" color="warning" text>Desactivar</v-btn>
                <v-btn v-if="adAccion == 3" @click="eliminar()" color="error" text>Eliminar</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Modal suspender próxima fecha -->
          <v-dialog v-model="suspendModal" max-width="460">
            <v-card>
              <v-card-title class="suspend-modal-title">
                <v-icon color="warning" class="mr-2">pause_circle</v-icon>
                Suspender próxima fecha
              </v-card-title>
              <v-card-text>
                <p class="suspend-modal-subtitle">
                  La actividad <strong>{{ adNombre }}</strong> no se realizará esa fecha.
                  La semana siguiente se retoma automáticamente.
                </p>
                <v-text-field
                  v-model="suspendDate"
                  label="Fecha a suspender"
                  type="date"
                  outlined
                  dense
                  class="mb-2"
                ></v-text-field>
                <v-text-field
                  v-model="suspendReason"
                  label="Motivo (visible para los miembros)"
                  placeholder="Ej: Por lluvia, Postergada, Feriado..."
                  outlined
                  dense
                  autofocus
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="suspendModal = false" text>Cancelar</v-btn>
                <v-btn
                  @click="suspenderFecha()"
                  color="warning"
                  :disabled="!suspendReason.trim() || !suspendDate || suspending"
                  text
                >
                  {{ suspending ? 'Suspendiendo...' : 'Confirmar suspensión' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Cards grid -->
          <div v-if="filteredActivities.length" class="activities-grid">
            <div
              v-for="item in filteredActivities"
              :key="item._id"
              class="activity-card"
              :class="{ 'card-inactive': !item.active }"
            >
              <!-- Date section -->
              <div class="card-date" :class="{ 'date-recurring': item.isRecurring }">
                <template v-if="item.isRecurring">
                  <v-icon small color="#a78bfa" class="mb-1">sync</v-icon>
                  <span class="date-recurring-day">{{ item.recurringDay }}</span>
                  <span class="date-recurring-time">{{ item.recurringStartTime }}</span>
                  <span class="date-recurring-sep">a</span>
                  <span class="date-recurring-time">{{ item.recurringEndTime }}</span>
                </template>
                <template v-else>
                  <span class="date-day">{{ getDay(item.date) }}</span>
                  <span class="date-month">{{ getMonth(item.date) }}</span>
                  <span class="date-weekday">{{ getWeekday(item.date) }}</span>
                  <span class="date-time">{{ formatTime(item.date) }}</span>
                </template>
              </div>

              <!-- Info section -->
              <div class="card-info">
                <div class="card-top-row">
                  <span class="type-badge">{{ item.type }}</span>
                  <span
                    class="status-badge"
                    :class="item.active ? 'status-active' : 'status-inactive'"
                  >
                    {{ item.active ? 'Activa' : 'Inactiva' }}
                  </span>
                </div>
                <h3 class="card-name">{{ item.name }}</h3>
                <p v-if="item.location" class="card-location">
                  <v-icon x-small color="#a78bfa">place</v-icon>
                  {{ item.location }}
                </p>
                <p v-if="item.description" class="card-description">{{ item.description }}</p>

                <!-- Admin Actions -->
                <div class="card-actions">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <button class="action-btn btn-edit" v-on="on" @click="edit(item)">
                        <v-icon small>edit</v-icon>
                      </button>
                    </template>
                    <span>Editar</span>
                  </v-tooltip>
                  <v-tooltip v-if="item.isRecurring" bottom>
                    <template v-slot:activator="{ on }">
                      <button class="action-btn btn-suspend" v-on="on" @click="mostrarSuspender(item)">
                        <v-icon small>pause_circle</v-icon>
                      </button>
                    </template>
                    <span>Suspender próxima fecha</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <button
                        v-if="item.active"
                        class="action-btn btn-visibility"
                        v-on="on"
                        @click="mostrarModal(2, item)"
                      >
                        <v-icon small>visibility_off</v-icon>
                      </button>
                      <button
                        v-else
                        class="action-btn btn-visibility"
                        v-on="on"
                        @click="mostrarModal(1, item)"
                      >
                        <v-icon small>visibility</v-icon>
                      </button>
                    </template>
                    <span>{{ item.active ? 'Desactivar' : 'Activar' }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <button class="action-btn btn-delete" v-on="on" @click="mostrarModal(3, item)">
                        <v-icon small>delete</v-icon>
                      </button>
                    </template>
                    <span>Eliminar</span>
                  </v-tooltip>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="tab !== 'pendientes'" class="empty-state">
            <v-icon large color="#a78bfa">event_busy</v-icon>
            <p>No hay actividades{{ tab !== 'todas' ? (tab === 'puntuales' ? ' puntuales' : ' recurrentes') : '' }}.</p>
            <button class="create-btn" @click="create()">+ Crear actividad</button>
          </div>

          <!-- Vista pendientes -->
          <div v-if="tab === 'pendientes'" class="pending-section">
            <div v-if="pendingActivities.length === 0" class="empty-state">
              <v-icon large color="#a78bfa">check_circle</v-icon>
              <p>No hay actividades pendientes de aprobación.</p>
            </div>
            <div v-else class="pending-list">
              <div v-for="act in pendingActivities" :key="act._id" class="pending-card">
                <div class="pending-card-body">
                  <div class="pending-card-top">
                    <span class="pending-type">{{ act.type }}</span>
                    <span class="pending-date">{{ formatPendingDate(act.date) }}</span>
                  </div>
                  <h3 class="pending-name">{{ act.name }}</h3>
                  <p class="pending-location">📍 {{ act.location }}{{ act.city ? ', ' + act.city : '' }}</p>
                  <p v-if="act.description" class="pending-desc">{{ act.description }}</p>
                </div>
                <div class="pending-card-actions">
                  <button class="btn-approve" @click="approve(act._id)">✓ Aprobar</button>
                  <button class="btn-reject" @click="openReject(act)">✗ Rechazar</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal rechazo -->
          <v-dialog v-model="rejectModal" max-width="400">
            <v-card style="padding: 20px;">
              <h3 style="margin:0 0 12px; font-size:1rem;">Rechazar actividad</h3>
              <p style="font-size:0.85rem; color:#64748b; margin:0 0 12px;">{{ rejectingAct && rejectingAct.name }}</p>
              <v-text-field
                v-model="rejectReason"
                label="Motivo del rechazo"
                placeholder="Ej: Fecha incorrecta, Falta información..."
                outlined dense
              />
              <div style="display:flex; gap:10px; justify-content:flex-end; margin-top:8px;">
                <v-btn text @click="rejectModal = false">Cancelar</v-btn>
                <v-btn color="error" :disabled="!rejectReason.trim()" @click="confirmReject">Rechazar</v-btn>
              </div>
            </v-card>
          </v-dialog>
        </div>
      </template>

      <!-- FORMULARIO DE EDICION / CREACION -->
      <template v-if="editing">
        <div class="activities-page">
          <div class="form-header">
            <button class="back-btn" @click="cancelEdit()">
              <v-icon small>arrow_back</v-icon> Volver
            </button>
            <h2 class="form-title">{{ form._id ? 'Editar actividad' : 'Nueva actividad' }}</h2>
          </div>
          <div class="form-card">
            <v-layout wrap class="form-grid">
              <v-flex xs12 sm6 md4>
                <v-text-field v-model="form.name" label="Nombre" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-select v-model="form.type" :items="types" label="Tipo" outlined dense></v-select>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field v-model="form.date" label="Fecha" type="date" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-select v-model="form.time" :items="timeOptions" label="Horario" outlined dense></v-select>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field v-model="form.location" label="Ubicación (dirección)" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field v-model="form.city" label="Ciudad / Localidad" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-select v-model="form.department" :items="departments" label="Departamento" outlined dense clearable></v-select>
              </v-flex>
              <v-flex xs12 sm6 md4>
                <v-text-field v-model="form.result" label="Resultado" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-textarea v-model="form.description" label="Descripción" rows="3" outlined dense></v-textarea>
              </v-flex>

              <v-flex xs12 sm6 md3>
                <v-checkbox v-model="form.isRecurring" label="Es recurrente" color="#8b5cf6"></v-checkbox>
              </v-flex>
              <template v-if="form.isRecurring">
                <v-flex xs12 sm6 md3>
                  <v-select v-model="form.recurringDay" :items="days" label="Día recurrente" outlined dense></v-select>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <v-text-field v-model="form.recurringStartTime" label="Hora inicio" type="time" outlined dense></v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <v-text-field v-model="form.recurringEndTime" label="Hora fin" type="time" outlined dense></v-text-field>
                </v-flex>
              </template>

              <v-flex xs12 class="form-actions">
                <button class="cancel-btn" @click="cancelEdit()">Cancelar</button>
                <button class="save-btn" @click="guardar()">Guardar</button>
              </v-flex>
            </v-layout>
          </div>
        </div>
      </template>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import swal from "sweetalert";

export default {
  data() {
    return {
      editing: false,
      search: "",
      tab: "todas",
      form: {},
      types: ["Reunion", "Evento", "Capacitacion", "Encuentro Regional", "Otro"],
      departments: ["Artigas","Canelones","Cerro Largo","Colonia","Durazno","Flores","Florida","Lavalleja","Maldonado","Montevideo","Paysandú","Río Negro","Rivera","Rocha","Salto","San José","Soriano","Tacuarembó","Treinta y Tres"],
      days: ["Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado", "Domingo"],
      timeOptions: (() => {
        const opts = [];
        for (let h = 8; h <= 23; h++) {
          opts.push(`${String(h).padStart(2, "0")}:00`);
          if (h < 23) opts.push(`${String(h).padStart(2, "0")}:30`);
        }
        return opts;
      })(),
      adModal: false,
      adAccion: 0,
      adNombre: "",
      adId: "",
      suspendModal: false,
      suspendReason: "",
      suspendDate: "",
      suspending: false,
      rejectModal: false,
      rejectingAct: null,
      rejectReason: "",
    };
  },
  created() {
    this.getActivities();
    this.getPending();
  },
  computed: {
    ...mapState("activityNamespace", ["activities", "pendingActivities"]),
    filteredActivities() {
      let list = this.activities || [];
      // Filter by tab
      if (this.tab === "puntuales") {
        list = list.filter((a) => !a.isRecurring);
      } else if (this.tab === "recurrentes") {
        list = list.filter((a) => a.isRecurring);
      }
      // Filter by search
      if (this.search) {
        const q = this.search.toLowerCase();
        list = list.filter(
          (a) =>
            (a.name && a.name.toLowerCase().includes(q)) ||
            (a.type && a.type.toLowerCase().includes(q)) ||
            (a.location && a.location.toLowerCase().includes(q))
        );
      }
      return list;
    },
  },
  methods: {
    ...mapActions("activityNamespace", [
      "getActivities",
      "saveActivity",
      "updateActivity",
      "activateActivity",
      "deactivateActivity",
      "deleteActivity",
      "getPendingActivities",
      "approveActivity",
      "rejectActivity",
    ]),
    getPending() {
      this.getPendingActivities();
    },
    approve(id) {
      this.approveActivity(id);
    },
    openReject(act) {
      this.rejectingAct = act;
      this.rejectReason = "";
      this.rejectModal = true;
    },
    confirmReject() {
      this.rejectActivity({ id: this.rejectingAct._id, reason: this.rejectReason });
      this.rejectModal = false;
      this.rejectingAct = null;
    },
    formatPendingDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleString('es-UY', { dateStyle: 'medium', timeStyle: 'short' });
    },
    create() {
      this.form = {
        name: "",
        description: "",
        type: "",
        date: "",
        time: "",
        location: "",
        city: "",
        department: "",
        result: "",
        isRecurring: false,
        recurringDay: "",
        recurringStartTime: "",
        recurringEndTime: "",
      };
      this.editing = true;
    },
    edit(item) {
      this.form = Object.assign({}, item);
      if (this.form.date) {
        const d = new Date(this.form.date);
        const pad = (n) => String(n).padStart(2, "0");
        this.form.date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        this.form.time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
      this.form = {};
    },
    async guardar() {
      const payload = Object.assign({}, this.form);
      if (payload.date && payload.time) {
        payload.date = `${payload.date}T${payload.time}`;
      }
      delete payload.time;
      if (payload._id) {
        await this.updateActivity(payload);
      } else {
        await this.saveActivity(payload);
      }
      this.editing = false;
      this.form = {};
    },
    mostrarModal(accion, item) {
      this.adModal = true;
      this.adAccion = accion;
      this.adNombre = item.name;
      this.adId = item._id;
    },
    activar() {
      this.activateActivity(this.adId);
      this.adModal = false;
    },
    desactivar() {
      this.deactivateActivity(this.adId);
      this.adModal = false;
    },
    eliminar() {
      this.deleteActivity(this.adId);
      this.adModal = false;
    },
    mostrarSuspender(item) {
      this.adId = item._id;
      this.adNombre = item.name;
      this.suspendReason = "";
      this.suspendDate = this.calcNextOccurrence(item.recurringDay);
      this.suspendModal = true;
    },
    calcNextOccurrence(dayName) {
      const dayMap = { lunes: 1, martes: 2, miercoles: 3, miércoles: 3, jueves: 4, viernes: 5, sabado: 6, sábado: 6, domingo: 0 };
      const target = dayMap[(dayName || "").toLowerCase()];
      if (target === undefined) return "";
      const now = new Date();
      let diff = target - now.getDay();
      if (diff <= 0) diff += 7;
      const next = new Date(now);
      next.setDate(now.getDate() + diff);
      const pad = n => String(n).padStart(2, "0");
      return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
    },
    async suspenderFecha() {
      if (!this.suspendReason.trim() || !this.suspendDate) return;
      this.suspending = true;
      const token = localStorage.getItem("token");
      try {
        await axios.post(
          `${(this.$store.state.appNamespace && this.$store.state.appNamespace.apiBase) || ""}activity/${this.adId}/cancellations`,
          { date: this.suspendDate, reason: this.suspendReason.trim() },
          { headers: { Authorization: token } }
        );
        this.suspendModal = false;
        swal({ title: "Listo", text: "La próxima fecha fue suspendida. La siguiente semana se retoma.", icon: "success" });
        this.getActivities();
      } catch (e) {
        swal({ title: "Error", text: (e && e.response && e.response.data && e.response.data.message) || "No se pudo suspender la fecha.", icon: "error" });
      } finally {
        this.suspending = false;
      }
    },
    // Date helpers
    getDay(dateStr) {
      if (!dateStr) return "--";
      return String(new Date(dateStr).getDate()).padStart(2, "0");
    },
    getMonth(dateStr) {
      if (!dateStr) return "";
      const months = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
      return months[new Date(dateStr).getMonth()];
    },
    getWeekday(dateStr) {
      if (!dateStr) return "";
      const days = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
      return days[new Date(dateStr).getDay()];
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      const h = String(d.getHours()).padStart(2, "0");
      const m = String(d.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    },
  },
};
</script>

<style scoped>
.activities-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

/* Header */
.page-header {
  text-align: center;
  margin-bottom: 32px;
}

.header-badge {
  display: inline-block;
  padding: 5px 14px;
  border-radius: 20px;
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
}

.page-title {
  font-size: 2rem;
  font-weight: 800;
  margin: 0 0 8px;
  background: linear-gradient(135deg, #7c3aed, #8b5cf6, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
}

/* Controls bar */
.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.tabs-wrapper {
  display: flex;
  gap: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(139, 92, 246, 0.2);
}

.tab-btn {
  padding: 10px 24px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn.active {
  background: #8b5cf6;
  color: white;
}

.tab-btn--pending { position: relative; }
.pending-badge {
  display: inline-flex; align-items: center; justify-content: center;
  background: #ef4444; color: white; font-size: 0.7rem; font-weight: 700;
  border-radius: 10px; padding: 1px 6px; margin-left: 6px; min-width: 18px;
}
.pending-section { margin-top: 8px; }
.pending-list { display: flex; flex-direction: column; gap: 12px; }
/* Estas tarjetas estaban pensadas para tema claro (fondo blanco, texto
   #1e293b, botones pastel). La app corre siempre en oscuro: se traducen a
   superficie oscura conservando el mismo significado de color — naranja para
   "pendiente", verde aprobar, rojo rechazar. */
.pending-card {
  display: flex; gap: 16px; align-items: flex-start;
  background: var(--lla-surface-2, #232030);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-left: 4px solid #f97316;
  border-radius: 12px; padding: 16px; flex-wrap: wrap;
}
.pending-card-body { flex: 1; min-width: 0; }
.pending-card-top { display: flex; gap: 10px; align-items: center; margin-bottom: 6px; flex-wrap: wrap; }
.pending-type { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.72); padding: 3px 10px; border-radius: 6px; font-size: 0.73rem; font-weight: 700; text-transform: uppercase; }
.pending-date { font-size: 0.8rem; color: rgba(255, 255, 255, 0.5); }
.pending-name { font-size: 1rem; font-weight: 700; color: rgba(255, 255, 255, 0.94); margin: 0 0 4px; }
.pending-location { font-size: 0.85rem; color: rgba(255, 255, 255, 0.66); margin: 0 0 4px; }
.pending-desc { font-size: 0.82rem; color: rgba(255, 255, 255, 0.5); margin: 4px 0 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.pending-card-actions { display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; }
.btn-approve { padding: 8px 16px; border: 1px solid rgba(129, 199, 132, 0.45); border-radius: 8px; font-size: 0.85rem; font-weight: 700; cursor: pointer; background: rgba(129, 199, 132, 0.16); color: #a5d6a7; transition: background 0.15s; }
.btn-approve:hover { background: rgba(129, 199, 132, 0.28); }
.btn-reject { padding: 8px 16px; border: 1px solid rgba(207, 102, 121, 0.45); border-radius: 8px; font-size: 0.85rem; font-weight: 700; cursor: pointer; background: rgba(207, 102, 121, 0.16); color: #ff8a9b; transition: background 0.15s; }
.btn-reject:hover { background: rgba(207, 102, 121, 0.28); }

.tab-btn:not(.active):hover {
  background: rgba(139, 92, 246, 0.08);
  color: #7c3aed;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-field {
  max-width: 260px;
}

.create-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 10px;
  background: #8b5cf6;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  white-space: nowrap;
}

.create-btn:hover {
  background: #7c3aed;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
}

/* Cards Grid */
.activities-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

/* Card */
.activity-card {
  display: grid;
  grid-template-columns: auto 1fr;
  border-radius: 14px;
  border: 1px solid rgba(139, 92, 246, 0.18);
  background: var(--lla-surface-1, #1c1826);
  overflow: hidden;
  transition: all 0.3s ease;
  animation: cardEnter 0.4s ease both;
}

.activity-card:hover {
  border-color: rgba(139, 92, 246, 0.35);
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(139, 92, 246, 0.12);
}

.activity-card.card-inactive {
  opacity: 0.6;
}

.activity-card.card-inactive:hover {
  opacity: 0.85;
}

@keyframes cardEnter {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Date column */
.card-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 18px;
  min-width: 90px;
  background: rgba(139, 92, 246, 0.06);
  border-right: 1px solid rgba(139, 92, 246, 0.08);
}

.card-date.date-recurring {
  background: rgba(139, 92, 246, 0.1);
}

.date-day {
  font-size: 2rem;
  font-weight: 800;
  color: #7c3aed;
  line-height: 1;
}

.date-month {
  font-size: 0.78rem;
  font-weight: 700;
  color: #8b5cf6;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 2px;
}

.date-weekday {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 2px;
}

.date-time {
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(139, 92, 246, 0.08);
}

.date-recurring-day {
  font-size: 0.85rem;
  font-weight: 700;
  color: #7c3aed;
  text-align: center;
}

.date-recurring-time {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
}

.date-recurring-sep {
  font-size: 0.7rem;
  color: #94a3b8;
}

/* Info column */
.card-info {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-top-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  background: rgba(139, 92, 246, 0.1);
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.status-badge {
  padding: 2px 10px;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
}

.status-active {
  background: rgba(34, 197, 94, 0.1);
  color: #81c784;
}

.status-inactive {
  background: rgba(239, 68, 68, 0.1);
  color: #ff8a9b;
}

.card-name {
  font-size: 1.1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.94);
  margin: 0;
  line-height: 1.3;
}

.card-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
}

.card-description {
  font-size: 0.82rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Card actions */
.card-actions {
  display: flex;
  gap: 6px;
  margin-top: auto;
  padding-top: 10px;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-edit {
  background: rgba(234, 179, 8, 0.1);
  color: #ca8a04;
}
.btn-edit:hover {
  background: rgba(234, 179, 8, 0.25);
  transform: translateY(-1px);
}

.btn-visibility {
  background: rgba(59, 130, 246, 0.1);
  color: #90caf9;
}
.btn-visibility:hover {
  background: rgba(59, 130, 246, 0.25);
  transform: translateY(-1px);
}

.btn-suspend {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}
.btn-suspend:hover {
  background: rgba(245, 158, 11, 0.25);
  transform: translateY(-1px);
}

.btn-delete {
  background: rgba(239, 68, 68, 0.1);
  color: #ff8a9b;
}
.btn-delete:hover {
  background: rgba(239, 68, 68, 0.25);
  transform: translateY(-1px);
}

.suspend-modal-title {
  font-size: 1rem !important;
  font-weight: 700;
}
.suspend-modal-subtitle {
  font-size: 0.88rem;
  color: #64748b;
  margin-bottom: 16px;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  border-radius: 14px;
  border: 1px dashed rgba(139, 92, 246, 0.2);
  background: rgba(139, 92, 246, 0.02);
}

.empty-state p {
  color: #94a3b8;
  font-size: 0.95rem;
  margin: 12px 0 16px;
}

/* Form */
.form-header {
  margin-bottom: 20px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  background: rgba(139, 92, 246, 0.08);
  color: #7c3aed;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
}

.back-btn:hover {
  background: rgba(139, 92, 246, 0.15);
}

.form-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.94);
  margin: 0;
}

.form-card {
  background: var(--lla-surface-1, #1c1826);
  border-radius: 14px;
  border: 1px solid rgba(139, 92, 246, 0.12);
  padding: 24px;
}

.form-grid {
  gap: 8px;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 12px;
}

.cancel-btn {
  padding: 10px 24px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: transparent;
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.24);
}

.save-btn {
  padding: 10px 28px;
  border: none;
  border-radius: 10px;
  background: #8b5cf6;
  color: white;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
}

.save-btn:hover {
  background: #7c3aed;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.3);
}

/* Dark theme support */
.theme--dark .activity-card {
  background: rgba(30, 30, 46, 0.7);
  border-color: rgba(139, 92, 246, 0.2);
}

.theme--dark .activity-card:hover {
  border-color: rgba(139, 92, 246, 0.45);
  box-shadow: 0 8px 30px rgba(139, 92, 246, 0.15);
}

.theme--dark .card-date {
  background: rgba(139, 92, 246, 0.1);
  border-right-color: rgba(139, 92, 246, 0.15);
}

.theme--dark .date-day {
  color: #ffd21c;
}

.theme--dark .date-month {
  color: #a78bfa;
}

.theme--dark .date-weekday {
  color: #e2e8f0;
}

.theme--dark .date-time {
  color: #cbd5e1;
  background: rgba(139, 92, 246, 0.15);
}

.theme--dark .card-name {
  color: #f1f5f9;
}

.theme--dark .card-location {
  color: #b0bac9;
}

.theme--dark .card-description {
  color: #94a3b8;
}

.theme--dark .page-subtitle {
  color: #b0bac9;
}

.theme--dark .type-badge {
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
}

.theme--dark .form-card {
  background: rgba(30, 30, 46, 0.7);
  border-color: rgba(139, 92, 246, 0.2);
}

.theme--dark .form-title {
  color: #f1f5f9;
}

.theme--dark .tab-btn {
  color: #94a3b8;
}

.theme--dark .tab-btn:not(.active):hover {
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
}

.theme--dark .tabs-wrapper {
  border-color: rgba(139, 92, 246, 0.3);
}

.theme--dark .empty-state {
  border-color: rgba(139, 92, 246, 0.25);
  background: rgba(139, 92, 246, 0.04);
}

.theme--dark .empty-state p {
  color: #b0bac9;
}

.theme--dark .cancel-btn {
  border-color: rgba(139, 92, 246, 0.2);
  color: #94a3b8;
}

.theme--dark .cancel-btn:hover {
  background: rgba(139, 92, 246, 0.08);
  border-color: rgba(139, 92, 246, 0.35);
}

.theme--dark .back-btn {
  background: rgba(139, 92, 246, 0.15);
  color: #a78bfa;
}

.theme--dark .date-recurring-day {
  color: #a78bfa;
}

.theme--dark .date-recurring-time {
  color: #cbd5e1;
}

/* Responsive */
@media (max-width: 960px) {
  .activities-grid {
    grid-template-columns: 1fr;
  }
  .controls-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .controls-right {
    flex-wrap: wrap;
  }
  .search-field {
    max-width: 100%;
    flex: 1;
  }
}

@media (max-width: 500px) {
  .activity-card {
    grid-template-columns: 1fr;
  }
  .card-date {
    flex-direction: row;
    gap: 10px;
    padding: 12px 18px;
    border-right: none;
    border-bottom: 1px solid rgba(139, 92, 246, 0.1);
  }
  .date-day {
    font-size: 1.5rem;
  }
  .activities-page {
    padding: 16px;
  }
  .page-title {
    font-size: 1.5rem;
  }
}
</style>
